import { useTheme } from "../ThemeContext";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import ShinyText from "../reactbits/ShinyText";
import { Handshake, Quote, TrendingUp } from "lucide-react";
import { evidenceStats, partners, testimonials } from "../../config/providerProof";

/** Evidence figures and testimonials. The page only mounts this when either list has content. */
export function EvidenceAndTestimonials() {
    const { theme } = useTheme();
    const heading = theme === "dark" ? "text-white" : "text-slate-900";
    const muted = theme === "dark" ? "text-slate-400" : "text-slate-600";
    const card = theme === "dark" ? "bg-slate-900/50 border-slate-800" : "bg-white border-slate-200";

    return (
        <div className="w-full max-w-6xl mx-auto py-14 md:py-10 md:py-24 px-4">
            <div className="text-center mb-6 md:mb-14">
                <Badge variant="outline" className="mb-4 px-4 py-2 bg-purple-500/10 border-purple-500/30">
                    <TrendingUp className="w-4 h-4 mr-2 text-purple-500" />
                    <ShinyText text="Evidence & Testimonials" speed={3} className="font-medium" />
                </Badge>
                <h2 className={`text-3xl md:text-5xl font-bold ${heading}`}>See it in practice</h2>
            </div>

            {evidenceStats.length > 0 && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
                    {evidenceStats.map(({ value, label, source }) => (
                        <Card key={label} className={`p-8 text-center border ${card}`}>
                            <div className="text-5xl font-bold bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent mb-2">
                                {value}
                            </div>
                            <p className={`font-medium ${heading}`}>{label}</p>
                            {source && <p className={`mt-2 text-xs ${muted}`}>{source}</p>}
                        </Card>
                    ))}
                </div>
            )}

            {testimonials.length > 0 && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {testimonials.map(({ quote, name, role, organization, avatar }) => (
                        <Card key={`${name}-${quote}`} className={`flex flex-col p-8 border ${card}`}>
                            <Quote className="w-8 h-8 mb-4 text-purple-500/60" />
                            <blockquote className={`grow leading-relaxed mb-6 ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                                {quote}
                            </blockquote>
                            <div className="flex items-center gap-3">
                                {avatar && (
                                    <img src={avatar} alt={name} className="w-11 h-11 rounded-full object-cover border border-purple-500/30" />
                                )}
                                <div>
                                    <p className={`font-semibold ${heading}`}>{name}</p>
                                    <p className={`text-sm ${muted}`}>
                                        {role}
                                        {organization ? `, ${organization}` : ""}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Partner and client logos. The page only mounts this when the list has content. */
export function PartnerLogos() {
    const { theme } = useTheme();

    return (
        <div className="w-full max-w-6xl mx-auto py-16 px-4 text-center">
            <Badge variant="outline" className="mb-8 px-4 py-2 bg-slate-500/10 border-slate-500/30">
                <Handshake className="w-4 h-4 mr-2 text-slate-500" />
                <ShinyText text="Partners & Clients" speed={4} className="font-medium" />
            </Badge>
            <ul className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
                {partners.map(({ name, logo, href }) => {
                    const content = logo ? (
                        <img
                            src={logo}
                            alt={name}
                            className="h-10 w-auto max-w-[160px] object-contain opacity-70 grayscale transition hover:opacity-100 hover:grayscale-0"
                        />
                    ) : (
                        <span className={`text-lg font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>{name}</span>
                    );
                    return (
                        <li key={name}>
                            {href ? (
                                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={name}>
                                    {content}
                                </a>
                            ) : (
                                content
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
